import { SETTINGS_SECTIONS, SettingsSection } from "../Settings.sections";
import { SettingsAccent } from "./settingsConfig";
import type { ButtonProps } from "components/common/buttons/Button";

// Centralized settings theming map to avoid scattering colors
export const SETTINGS_THEME = SETTINGS_SECTIONS.reduce<Record<string, { accent: string }>>(
  (acc, s) => {
    acc[s.id] = { accent: s.accent };
    return acc;
  },
  {}
);

// Helper to get accent by section id
export const getSettingsAccent = (id: string, fallback: string = "indigo") => {
  return SETTINGS_THEME[id]?.accent ?? fallback;
};

// Type for valid ACCENT_TOKENS keys (return type of getValidAccentTokenKey)
export type AccentTokenKey =
  | "indigo"
  | "violet"
  | "emerald"
  | "cyan"
  | "teal"
  | "blue"
  | "red"
  | "gray"
  | "brand";

// Tokens for accent -> utility classes used in UI (texts, soft backgrounds, borders)
export const ACCENT_TOKENS: Record<
  AccentTokenKey,
  { text: string; bgSoft: string; border: string }
> = {
  indigo: {
    text: "text-hue-indigo",
    bgSoft: "bg-hue-indigo/10",
    border: "border-hue-indigo/20",
  },
  emerald: {
    text: "text-hue-emerald",
    bgSoft: "bg-hue-emerald/10",
    border: "border-hue-emerald/20",
  },
  blue: {
    text: "text-hue-blue",
    bgSoft: "bg-hue-blue/10",
    border: "border-hue-blue/20",
  },
  teal: {
    text: "text-hue-teal",
    bgSoft: "bg-hue-teal/10",
    border: "border-hue-teal/20",
  },
  cyan: {
    text: "text-hue-cyan",
    bgSoft: "bg-hue-cyan/10",
    border: "border-hue-cyan/20",
  },
  violet: {
    text: "text-hue-violet",
    bgSoft: "bg-hue-violet/10",
    border: "border-hue-violet/20",
  },
  brand: {
    text: "text-primary",
    bgSoft: "bg-primary/10",
    border: "border-primary/20",
  },
  gray: {
    text: "text-muted-foreground",
    bgSoft: "bg-muted",
    border: "border-border",
  },
  red: {
    text: "text-danger",
    bgSoft: "bg-danger/10",
    border: "border-danger/20",
  },
};

export const useSettingsAccent = (sectionId: SettingsSection["id"]) => {
  const accent = getSettingsAccent(sectionId) as SettingsAccent;
  const tokens = ACCENT_TOKENS[accent];
  return { accent, tokens };
};

/**
 * Type guard to check if a string is a valid AccentTokenKey
 */
export const isValidAccentTokenKey = (value: string | undefined): value is AccentTokenKey => {
  return value !== undefined && value in ACCENT_TOKENS;
};

/**
 * Gets a valid AccentTokenKey from a workspace accent, with fallback
 */
export const getValidAccentTokenKey = (
  accent: string | undefined,
  fallback: AccentTokenKey = "indigo"
): AccentTokenKey => {
  return isValidAccentTokenKey(accent) ? accent : fallback;
};

/**
 * Gets a valid ButtonProps color from a workspace accent, with fallback
 */
export const getValidButtonColor = (
  accent: string | undefined,
  fallback: ButtonProps["color"] = "indigo"
): ButtonProps["color"] => {
  // ButtonProps["color"] includes all SettingsAccent values plus some extras
  // If it's a valid accent token key, it's also a valid button color
  if (isValidAccentTokenKey(accent)) {
    return accent as ButtonProps["color"];
  }
  return fallback;
};

/**
 * Gets all accent-related values (color, token key, and tokens) in one call
 * This is more efficient than calling multiple functions separately
 */
export const getAccentValues = (
  accent: string | undefined,
  fallback: AccentTokenKey = "indigo"
): {
  color: ButtonProps["color"];
  tokenKey: AccentTokenKey;
  tokens: (typeof ACCENT_TOKENS)[AccentTokenKey];
} => {
  const tokenKey = getValidAccentTokenKey(accent, fallback);
  const color = getValidButtonColor(accent, fallback);
  const tokens = ACCENT_TOKENS[tokenKey];
  return { color, tokenKey, tokens };
};
